import type { RefObject } from "react";
import { useEffect } from "react";

interface UseClickOutside {
  onClickOutside: () => void;
  ref: RefObject<HTMLElement>;
  closeOnEsc?: boolean;
  closeOnOutside?: boolean;
  isOpen?: boolean;
}

const useClickOutside = ({
  onClickOutside,
  ref,
  closeOnEsc,
  closeOnOutside,
  isOpen,
}: UseClickOutside) => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | KeyboardEvent) => {
      if (ref?.current && !ref?.current.contains(event.target as Node)) {
        onClickOutside && onClickOutside();
      }
    };

    const handleClickEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClickOutside?.();
      }
    };

    isOpen &&
      closeOnOutside &&
      document.addEventListener("click", handleClickOutside, true);
    isOpen &&
      closeOnEsc &&
      document.addEventListener("keydown", handleClickEscape);

    return () => {
      document.removeEventListener("click", handleClickOutside, true);
      closeOnEsc && document.removeEventListener("keydown", handleClickEscape);
    };
  }, [closeOnEsc, onClickOutside, ref, isOpen, closeOnOutside]);
};

export default useClickOutside;
